import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchPublicProfiles } from '../utils/publicProfilesBatch';
import { buildMentionExemptions } from '../utils/commentMentions';

// Shared so the "no names" value is the same object every render (stable
// hook dependencies).
const NO_NAMES = Object.freeze({});

/**
 * Canonical usernames of the people on one listing's comment thread (the
 * owner and every commenter), plus names picked from the @ popup. Used for
 * two things on PedalDetails: which @names the content filter may skip, and
 * the name Reply inserts.
 *
 * Why it is a hook (2026-10-06 review): the stored `username` on a comment
 * can be the writer's EMAIL (the comment writer falls back to it when the
 * profile has not loaded), so names must come from publicProfiles by uid.
 * And the names belong to ONE listing. PedalDetails is reused across
 * /listing/:id navigations, so names held in plain component state outlived
 * the listing; if the next listing's profile lookup failed, the previous
 * listing's names stayed exempt from the filter and available to Reply.
 *
 * Three rules keep names inside their listing:
 * 1. Participant ids are derived HERE, only from data that belongs to
 *    `listingId`: the owner only when `pedal.id === listingId`, commenters
 *    only from comments whose `listingId` matches. PedalDetails clears the
 *    previous pedal/comments in an effect, so for at least one render after
 *    navigating, the new id sits beside the OLD listing's pedal and
 *    comments; those must not be loaded and stamped as the new listing's.
 * 2. Loaded names are stamped with the listing id they were built for and
 *    read back only when the stamp matches.
 * 3. Every listing change (including a return to a listing visited before)
 *    resets loaded AND picked names in the same render, before any effect
 *    runs and regardless of whether exemptions are read on the way.
 *
 * @param {string|undefined} listingId
 * @param {{id?: string, ownerId?: string}|null} pedal  the loaded listing
 * @param {Array<{listingId?: string, userId?: string}>} comments
 */
export function useCommentParticipants(listingId, pedal, comments) {
  const ownerId = pedal && pedal.id === listingId ? pedal.ownerId : null;
  const commenterIds = (comments || [])
    .filter((c) => c && c.listingId === listingId)
    .map((c) => c.userId);
  const idsKey = [ownerId, ...commenterIds]
    .filter(Boolean)
    .filter((id, i, all) => all.indexOf(id) === i)
    .sort()
    .join(',');

  // { listingId, names: { uid: username } }
  const [loaded, setLoaded] = useState({ listingId: null, names: {} });
  // { listingId, names: Set<string> }
  const pickedRef = useRef({ listingId, names: new Set() });

  // Rule 3: render-phase reset on every listing change (React's documented
  // pattern for resetting state when a prop changes). Idempotent, so a
  // repeated render pass (StrictMode) is harmless.
  const [seenListingId, setSeenListingId] = useState(listingId);
  if (seenListingId !== listingId) {
    setSeenListingId(listingId);
    setLoaded({ listingId: null, names: {} });
    pickedRef.current = { listingId, names: new Set() };
  }

  useEffect(() => {
    if (!listingId || !idsKey) return undefined;
    let cancelled = false;
    fetchPublicProfiles(idsKey.split(','), { fallback: {} })
      .then((profiles) => {
        if (cancelled) return;
        const names = {};
        for (const [uid, prof] of Object.entries(profiles || {})) {
          if (typeof prof?.username === 'string' && prof.username.trim()) names[uid] = prof.username;
        }
        // MERGE into names already verified for THIS listing, never replace
        // (review finding, 2026-10-06). fetchPublicProfiles does not reject:
        // a failed chunk is logged and its ids come back as the fallback
        // ({} here, i.e. no username). So a failed refetch, e.g. when a new
        // person comments on a flaky connection, used to look like "nobody
        // has a name" and wiped the names Reply and the filter depend on.
        // Fresh names still win, so a renamed user updates. Another
        // listing's names can't leak in: the reset above empties `loaded`
        // on every listing change, so `prev` is only ever this listing's.
        setLoaded((prev) => (prev.listingId === listingId
          ? { listingId, names: { ...prev.names, ...names } }
          : { listingId, names }));
      })
      .catch((err) => {
        // Defensive only: fetchPublicProfiles handles its own failures.
        // Leave whatever this LISTING already had; never another listing's.
        console.error('Comment participant profile load failed:', err);
      });
    return () => { cancelled = true; };
  }, [listingId, idsKey]);

  const participantUsernames = loaded.listingId === listingId ? loaded.names : NO_NAMES;

  const addPickedName = useCallback((name) => {
    if (pickedRef.current.listingId === listingId) pickedRef.current.names.add(name);
  }, [listingId]);

  const mentionExemptions = useCallback(() => buildMentionExemptions({
    profileUsernames: participantUsernames,
    pickedNames: pickedRef.current.listingId === listingId ? pickedRef.current.names : [],
  }), [participantUsernames, listingId]);

  return { participantUsernames, addPickedName, mentionExemptions };
}
